import React from "react";
import {
  FilterChipCondition,
  FilterChipConditionSeparator,
  FilterChipPart,
  FilterChipParts,
} from "./StoryFilterBar.styles";
import type {
  StoryFilterBarAppliedFilter,
  StoryFilterBarTone,
  StoryFilterChipPartId,
} from "./StoryFilterBar.types";

const PART_IDS: StoryFilterChipPartId[] = ["field", "operator", "value"];
const CONDITION_SEPARATOR = " · ";

export interface StoryFilterChipPartsProps {
  filter: StoryFilterBarAppliedFilter;
  tone?: StoryFilterBarTone;
  canEdit: boolean;
  /** Tooltip for a part that can't be edited. */
  lockedTitle: string;
  openPart?: StoryFilterChipPartId | null;
  onPartClick?: (part: StoryFilterChipPartId, conditionIndex: number) => void;
  /** Anchor for a part of the first condition. */
  getPartRef?: (part: StoryFilterChipPartId) => React.Ref<HTMLButtonElement>;
}

/**
 * A chip's `field | operator | value` as three click targets per condition, each with its own
 * hover. A part is plain text when the viewer can't edit, when it is listed in `lockedParts`, or
 * for every condition after the first (an edit changes the first condition only).
 */
export const StoryFilterChipParts: React.FC<StoryFilterChipPartsProps> = ({
  filter,
  tone,
  canEdit,
  lockedTitle,
  openPart = null,
  onPartClick,
  getPartRef,
}) => (
  <FilterChipParts data-testid="story-filter-chip-parts" title={filter.title}>
    {(filter.conditions ?? []).map((condition, index) => (
      // eslint-disable-next-line react/no-array-index-key -- conditions have no id and never reorder
      <React.Fragment key={index}>
        {index > 0 && <FilterChipConditionSeparator aria-hidden>{CONDITION_SEPARATOR}</FilterChipConditionSeparator>}
        <FilterChipCondition data-testid="story-filter-chip-condition">
          {PART_IDS.map((part) => {
            const isLocked = !canEdit || index > 0 || Boolean(filter.lockedParts?.includes(part));
            return (
              <FilterChipPart
                key={part}
                type="button"
                data-testid={`story-filter-chip-${part}`}
                $tone={tone}
                $quiet={part !== "value"}
                $isOpen={index === 0 && openPart === part}
                ref={index === 0 ? getPartRef?.(part) : undefined}
                disabled={isLocked}
                title={isLocked && canEdit ? filter.lockedTitle ?? lockedTitle : undefined}
                aria-haspopup={isLocked ? undefined : "listbox"}
                aria-expanded={isLocked ? undefined : index === 0 && openPart === part}
                onClick={() => onPartClick?.(part, index)}
              >
                {condition[part]}
              </FilterChipPart>
            );
          })}
        </FilterChipCondition>
      </React.Fragment>
    ))}
  </FilterChipParts>
);

export default StoryFilterChipParts;
